import React, { useState, useEffect } from 'react'
import {
  Typography,
  LinearProgress,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Modal,
  Button,
  Grid,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormHelperText
} from '@mui/material'
import { LinearProgressProps } from '@mui/material/LinearProgress'
import { SelectChangeEvent } from '@mui/material/Select'
import { UploadFile } from "@mui/icons-material"

import { post, customPost, put, postFile } from '../../../utils/http'
import { modalStyle } from '../../../styles/js/common'
import { FullNodeProp } from '../../../utils/types'
import { AxiosRequestConfig } from 'axios'

const LinearProgressWithLabel = (props: LinearProgressProps & { value: number }) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box sx={{ width: '100%', mr: 1 }}>
        <LinearProgress variant="determinate" color={props.color} value={props.value} sx={{ height: 8, borderRadius: 4 }} />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
          {`${Math.round(props.value)}%`}
        </Typography>
      </Box>
    </Box>
  )
}

interface FileUploadModalProp {
  open: boolean,
  closeModal: Function,
  saveFile: Function,
  saveMounts: Function,
  selectedNode: FullNodeProp
}

interface slideInfoProp {
  patient_id: string,
  patient_name: string,
  patient_status: string,
  cancer_type: string,
  slide_type: string,
  bucket_name: string,
  bucket_region: string,
  bucket_dir: string,
  output_s3: string,
  output_s3_dir: string
}

const UploadSlide = ({ open, closeModal, saveFile, saveMounts, selectedNode }: FileUploadModalProp) => {
  const [slideInfo, setSlideInfo] = useState<slideInfoProp>({
    patient_id: '',
    patient_name: '',
    patient_status: 'treatment',
    cancer_type: 'prostate',
    slide_type: 'biopsy',
    bucket_name: '',
    bucket_region: 'us-west-2',
    bucket_dir: '',
    output_s3: '',
    output_s3_dir: ''
  })
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null)
  const [csvFile, setCsvFile] = useState<File | null>(null)

  // Feedback states
  const [message, setMessage] = useState<string>('')
  const [uploadError, setUploadError] = useState<boolean>(false)
  const [slideFileError, setSlideFileError] = useState<string>('')
  const [csvError, setCsvError] = useState<string>('')

  // Progress and lifecycle states
  const [uploadProgress, setUploadProgress] = useState<{ [key: number]: number }>({})
  const [totalUploadProgress, setTotalUploadProgress] = useState<number>(0)
  const [enableUploadBtn, setEnableUploadBtn] = useState<boolean>(false)
  const [uploadLimitWarn, setUploadLimitWarn] = useState<boolean>(false)
  const [isUploading, setIsUploading] = useState<boolean>(false)

  const supportedSlides = ['svs', 'tif', 'tiff', 'ndpi', 'mrxs', 'vms', 'vmu', 'scn', 'bif']
  const wsiFormats = supportedSlides.map(ext => `.${ext}`).join(', ')
  const userId = Number(localStorage.getItem('user_id') || '1')
  const location = selectedNode.name.startsWith('My Folder/') ? 'local' : 's3'

  useEffect(() => {
    const progressValues = Object.values(uploadProgress)
    const fileCount = progressValues.length
    if (!fileCount) return
    const sum = progressValues.reduce((acc, curr) => acc + curr, 0)
    const averageProgress = (sum / (fileCount * 100)) * 100
    setTotalUploadProgress(Math.max(1, averageProgress))
  }, [uploadProgress])

  const handleSlideInfoChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>) => {
    setMessage('')
    setSlideInfo(prevState => ({ ...prevState, [e.target.name]: e.target.value }))
  }

  const uploadFileChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessage('')
    setSlideFileError('')
    setSelectedFiles(e.target.files)
  }

  const handleS3FileListRef = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessage('')
    setCsvError('')
    const file = e.target.files?.[0]
    const maxSize = 2 * 1024 * 1024 // 2MB

    if (!file) {
      setCsvFile(null)
    } else if (file.size > maxSize) {
      setCsvError('CSV files up to 2MB only')
      setCsvFile(null)
    } else {
      setCsvFile(file)
    }
  }

  const verifyFormData = () => {
    // Mandatory base fields check
    const isBaseValid = true //slideInfo.patient_id.trim().length > 0

    if (location === 'local') {
      if (selectedFiles && selectedFiles.length > 0) {
        const validSlides = Array.from(selectedFiles).every((i: File) => {
          const t = i.name.split('.').pop()?.toLowerCase() || 'svs'
          return supportedSlides.includes(t) && !/\//.test(i.name)
        })

        if (validSlides && isBaseValid) {
          setEnableUploadBtn(true)
          setSlideFileError('')
        } else {
          setEnableUploadBtn(false)
          if (!validSlides) setSlideFileError(`Only ${wsiFormats} are supported`)
        }
      } else {
        setEnableUploadBtn(false)
      }
    } else {
      // S3 Validation
      if (slideInfo.bucket_name.trim().length > 0 && isBaseValid && !csvError) {
        setEnableUploadBtn(true)
      } else {
        setEnableUploadBtn(false)
      }
    }
  }

  useEffect(() => {
    verifyFormData()
  }, [slideInfo, selectedFiles, csvFile, csvError]) // eslint-disable-line

  const showUploadLimitWarn = () => setUploadLimitWarn(true)

  const upload = async (file: File, onUploadProgress: (progressEvent: ProgressEvent) => void) => {
    try {
      setIsUploading(true)
      const t = file.name.split('.').pop() || ''
      const payload = {
        filename: file.name,
        user_id: userId,
        file_type: t.length ? t.toLowerCase() : 'svs',
        patient_id: slideInfo.patient_id.trim(),
        patient_name: slideInfo.patient_name.trim(),
        patient_status: slideInfo.patient_status,
        created_by: userId,
        slide_type: slideInfo.cancer_type === 'prostate' ? slideInfo.slide_type : '',
        cancer_type: slideInfo.cancer_type,
        parent_node: selectedNode
      }

      const imgRes = await post('Images', payload)
      if (imgRes.limit_exceeded) {
        showUploadLimitWarn()
        throw Error('Upload limit exceeded')
      }

      const s3FormData = new FormData()
      Object.keys(imgRes.response.fields).forEach(key => s3FormData.append(key, imgRes.response.fields[key]))
      s3FormData.append('file', file)

      const config: AxiosRequestConfig = {
        onUploadProgress,
        headers: { 'Content-Type': 'multipart/form-data' }
      }
      await customPost(imgRes.response.url, s3FormData, config)

      await put(`Images/${imgRes.id}`, { status: 'Uploaded' })
      saveFile(imgRes.node_data)
      return true
    } catch (err) {
      console.error(err)
      return false
    }
  }

  const uploadFile = async (slide: File, id: number) => {
    return await upload(slide, (e: ProgressEvent) => {
      setUploadProgress(prevState => ({ ...prevState, [id]: Math.round((100 * e.loaded) / e.total) }))
    })
  }

  const handleSubmitForm = async (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    setTotalUploadProgress(1)

    if (location === 'local') {
      try {
        const upFiles = Array.from(selectedFiles!)
        const uploadPromises = upFiles.map((i: File, index: number) => uploadFile(i, index))
        const results = await Promise.all(uploadPromises)

        if (results.every((j: boolean) => j)) {
          setMessage('Upload completed successfully')
          setUploadError(false)
        } else {
          setMessage('1 or more files failed to upload')
          setUploadError(true)
        }
      } catch (err) {
        setMessage('Upload failed')
        setUploadError(true)
      } finally {
        setIsUploading(false)
        setSelectedFiles(null)
        setTotalUploadProgress(100)
      }
    } else {
      let percentage = 10
      const interval = setInterval(() => {
        if (percentage > 90) clearInterval(interval)
        setTotalUploadProgress(percentage)
        percentage += 10
      }, 2000)

      try {
        setMessage('Data is processing, please wait...')
        const tPath = slideInfo.output_s3_dir.replace(/^\/+|\/+$/g, '')
        const outputS3 = slideInfo.output_s3.replace(/^\/+|\/+$/g, '') + (tPath.length ? `/${tPath}` : '')

        const payload = new FormData()
        payload.append('patient_id', slideInfo.patient_id.trim())
        payload.append('patient_name', slideInfo.patient_name.trim())
        payload.append('patient_status', slideInfo.patient_status)
        payload.append('created_by', String(userId))
        payload.append('s3_name', slideInfo.bucket_name)
        payload.append('s3_dir', slideInfo.bucket_dir.replace(/^\/+|\/+$/g, ''))
        payload.append('cancer_type', slideInfo.cancer_type)
        payload.append('slide_type', slideInfo.cancer_type === 'prostate' ? slideInfo.slide_type : '')
        payload.append('user_id', JSON.stringify(userId))
        payload.append('parent_node', JSON.stringify(selectedNode))

        if (outputS3.length) payload.append('output_s3', outputS3)
        if (csvFile) payload.append('csv_file', csvFile)

        const res = await postFile('Images/s3', payload)
        if (res.limit_exceeded) {
          showUploadLimitWarn()
          throw Error('Upload limit exceeded')
        } else {
          saveMounts(res)
          setMessage('Upload completed successfully')
          setUploadError(false)
        }
      } catch (err) {
        setMessage('Upload failed')
        setUploadError(true)
      } finally {
        setCsvFile(null)
        clearInterval(interval)
        setTotalUploadProgress(100)
        setIsUploading(false)
      }
    }
  }

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => { if (isUploading) e.preventDefault() }
    if (isUploading) window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isUploading])

  const handleClose = (event?: any, reason?: string) => {
    if (isUploading || reason === 'backdropClick') return
    setTotalUploadProgress(0)
    setUploadLimitWarn(false)
    setUploadError(false)
    setSlideFileError('')
    setCsvError('')
    closeModal()
  }

  return (
    <>
      <Modal open={open || isUploading} onClose={handleClose}>
        <Box sx={{
          ...modalStyle,
          width: { xs: '95%', sm: '80%', md: 600 },
          maxWidth: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          p: { xs: 3, md: 4 },
          borderRadius: 2
        }}>
          <Stack spacing={4}>
            {totalUploadProgress > 0 ? (
              <Stack spacing={3}>
                <Typography variant="h5" component="h1" fontWeight="bold">
                  {message.length ? message : 'Uploading Slides...'}
                </Typography>
                <LinearProgressWithLabel
                  color={message.length ? (uploadError ? 'error' : 'success') : 'primary'}
                  value={totalUploadProgress}
                />
                {(totalUploadProgress === 100 || uploadError) && (
                  <Box display="flex" justifyItems="flex-end" justifyContent="flex-end">
                    <Button variant="outlined" onClick={handleClose}>Close</Button>
                  </Box>
                )}
              </Stack>
            ) : (
              <Stack spacing={3}>
                <Typography variant="h5" component="h1" fontWeight="bold">
                  {location === 'local' ? 'Upload Slides' : 'Mount S3 Bucket'}
                </Typography>

                <Box component="form" noValidate>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Patient ID"
                        variant="outlined"
                        name="patient_id"
                        value={slideInfo.patient_id}
                        onChange={handleSlideInfoChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Patient Name"
                        variant="outlined"
                        name="patient_name"
                        value={slideInfo.patient_name}
                        onChange={handleSlideInfoChange}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required>
                        <InputLabel id="cancer-type-select-label">Tissue Type</InputLabel>
                        <Select
                          labelId="cancer-type-select-label"
                          value={slideInfo.cancer_type}
                          label="Tissue Type"
                          name="cancer_type"
                          onChange={handleSlideInfoChange}
                        >
                          <MenuItem value='prostate'>Prostate</MenuItem>
                          <MenuItem value='lung'>Lung</MenuItem>
                          <MenuItem value='breast'>Breast</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required>
                        <InputLabel id="slide-type-select-label">Specimen Type</InputLabel>
                        <Select
                          labelId="slide-type-select-label"
                          value={slideInfo.slide_type}
                          label="Specimen Type"
                          name="slide_type"
                          onChange={handleSlideInfoChange}
                          disabled={slideInfo.cancer_type !== 'prostate'}
                        >
                          <MenuItem value='biopsy'>Biopsy</MenuItem>
                          <MenuItem value='rp'>RP</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {location === 'local' ? (
                      <Grid item xs={12}>
                        <FormControl fullWidth error={!!slideFileError}>
                          <Button
                            startIcon={<UploadFile />}
                            variant="outlined"
                            component="label"
                            sx={{ p: 2, borderStyle: 'dashed' }}
                          >
                            Browse Slides to Upload
                            <input
                              onChange={uploadFileChangeHandler}
                              name='upload-file'
                              type="file"
                              accept={wsiFormats}
                              multiple
                              hidden
                            />
                          </Button>
                          {selectedFiles && selectedFiles.length > 0 && (
                            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                              {selectedFiles.length} file(s) selected
                            </Typography>
                          )}
                          {slideFileError && <FormHelperText>{slideFileError}</FormHelperText>}
                        </FormControl>
                      </Grid>
                    ) : (
                      <>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Bucket Name"
                            variant="outlined"
                            name="bucket_name"
                            value={slideInfo.bucket_name}
                            onChange={handleSlideInfoChange}
                            required
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Directory"
                            variant="outlined"
                            name="bucket_dir"
                            value={slideInfo.bucket_dir}
                            onChange={handleSlideInfoChange}
                            disabled={!!csvFile}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <FormControl fullWidth error={!!csvError}>
                            <Button
                              startIcon={<UploadFile />}
                              variant="outlined"
                              component="label"
                              title='A CSV file containing a list of files in the Amazon S3'
                              sx={{ py: 1.5, borderStyle: 'dashed' }}
                            >
                              Upload S3 Reference CSV
                              <input onChange={handleS3FileListRef} name='s3_file_ref' type="file" accept='.csv' hidden />
                            </Button>
                            {csvFile && (
                              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                Selected: {csvFile.name}
                              </Typography>
                            )}
                            {csvError && <FormHelperText>{csvError}</FormHelperText>}
                          </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Output S3 Configuration (Optional)
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Output Name"
                            variant="outlined"
                            name="output_s3"
                            value={slideInfo.output_s3}
                            onChange={handleSlideInfoChange}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Output Path"
                            variant="outlined"
                            name="output_s3_dir"
                            value={slideInfo.output_s3_dir}
                            onChange={handleSlideInfoChange}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Box>

                <Box display="flex" gap={2} justifyContent="flex-end" pt={2}>
                  <Button variant="text" onClick={handleClose} disabled={isUploading}>Cancel</Button>
                  <Button
                    variant="contained"
                    type="submit"
                    onClick={handleSubmitForm}
                    disabled={!enableUploadBtn || isUploading}
                    sx={{ px: 4 }}
                  >
                    {location === 'local' ? 'Upload' : 'Mount'}
                  </Button>
                </Box>
              </Stack>
            )}
          </Stack>
        </Box>
      </Modal>

      <Dialog
        open={uploadLimitWarn}
        onClose={() => setUploadLimitWarn(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Upload Limit Exceeded</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have exceeded your allowed upload limits. To continue uploading files, please contact your administrator.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadLimitWarn(false)} variant="contained" color="primary">
            Acknowledge
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default UploadSlide